import {
  collection,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { useEffect, useState } from "react";
import { auth } from "@/api/firebase";
import { db } from "@/api/firestore";
import { createProtoFromFirestore } from "@/lib/proto";
import { MessageSchema } from "@generated/proto/model";
import type { Message } from "@generated/proto/model";

interface MessageRow {
  id: string;
  proto: Message;
  created?: Date;
  modified?: Date;
}

export function useChatFirestore(tripId?: string, threadId?: string) {
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tripId || !threadId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      setError("User not authenticated");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Create Firestore query for messages ordered by creation time
    const messagesRef = collection(
      db, 
      `users/${user.uid}/trips/${tripId}/threads/${threadId}/messages`
    );
    const q = query(messagesRef, orderBy("created", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        try {
          const data = snapshot.docs.map((doc) => {
            const raw: any = doc.data();
            
            // Fix oneof union reconstruction before creating proto
            const processedRaw = { ...raw };
            
            // Handle text_content oneof case
            if (raw.text_content && !raw.content) {
              processedRaw.content = {
                case: 'textContent',
                value: raw.text_content
              };
              // Remove the raw field to avoid conflicts
              delete processedRaw.text_content;
            }
            
            // Handle other oneof cases if they exist
            if (raw.tool_call && !raw.content) {
              processedRaw.content = {
                case: 'toolCall', 
                value: raw.tool_call
              };
              delete processedRaw.tool_call;
            }
            
            if (raw.tool_response && !raw.content) {
              processedRaw.content = {
                case: 'toolResponse',
                value: raw.tool_response  
              };
              delete processedRaw.tool_response;
            }
            
            if (raw.agent_error && !raw.content) {
              processedRaw.content = {
                case: 'agentError',
                value: raw.agent_error
              };
              delete processedRaw.agent_error;
            }
            
            
            const proto = createProtoFromFirestore<Message>(MessageSchema, processedRaw);
            return {
              id: doc.id,
              proto,
              created: raw.created?.toDate?.(),
              modified: raw.modified?.toDate?.(),
            } satisfies MessageRow;
          });
          setMessages(data);
          setLoading(false);
        } catch (err: any) {
          setError("Failed to load messages");
          console.error("[useChatFirestore] error parsing messages:", err);
          setLoading(false);
        }
      },
      (err) => {
        setError("Firestore subscription failed");
        console.error("[useChatFirestore] subscription error:", err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [tripId, threadId]);

  return {
    messages,
    loading,
    error,
  };
}